// Batch 2B-a — #devs-launch composer + live session feed.
//
// Rendered as the body of the #devs-launch channel (hardcoded route in
// content-area.ts). The top of the channel is a sticky "New dev session"
// composer card; below it is a newest-first feed of dev_sessions in this
// channel, each one a one-line summary + status dot + open-thread link.
//
// Colour tokens only — matches the 5-colour palette from Priority 2
// (statusToColor() in channels/dev-sessions.ts). No hardcoded hex.

import { state } from '../state';
import { get, post } from '../api';
import { showToast } from '../components/toast';
import { confirmModal } from '../components/modal';
import { openThreadPanelById } from './threads';
import { parseIsoUtc } from '../util';
import {
  listDevSessions,
  unArchiveDevSession,
  retryMcpWriteback,
  type DevSession,
  type DevStatusColor,
} from './dev-sessions';

interface BriefListing {
  path: string;
  title: string;
  preview: string;
  size_bytes: number;
  modified_at: string | null;
}

interface WorkerSlot {
  name: string;
  state: 'idle' | 'busy' | 'unhealthy' | 'unreachable';
  current_session_id: string | null;
  current_session_title: string | null;
  last_health_check: string | null;
  unhealthy_reason: string | null;
  pid: number | null;
  memory_mcp_ok: boolean;
  started_at: string | null;
}

interface WorkersSnapshot {
  workers: WorkerSlot[];
  queue_depth: number;
}

type WorkerHint = 'dev1' | 'dev2' | 'any-idle';
type PermissionMode = 'full_write' | 'read_plan' | 'ask_per_action';
type ComposerTab = 'inline' | 'file';

interface ComposerState {
  title: string;
  tab: ComposerTab;
  briefInline: string;
  briefPath: string | null;
  briefFilter: string;
  worker: WorkerHint;
  permission: PermissionMode;
  submitting: boolean;
}

const defaultState = (): ComposerState => ({
  title: '',
  tab: 'inline',
  briefInline: '',
  briefPath: null,
  briefFilter: '',
  worker: 'any-idle',
  permission: 'full_write',
  submitting: false,
});

let composerState: ComposerState = defaultState();
let briefsCache: BriefListing[] | null = null;
let feedTimer: ReturnType<typeof setInterval> | null = null;
let workersCache: WorkersSnapshot | null = null;
let workersTimer: ReturnType<typeof setInterval> | null = null;

// ── Archived section state ────────────────────────────────────────────────
// Cache archived rows for 60s so scrolling back to the panel doesn't re-hit
// the backend every time. Invalidated on un-archive / retry-mcp success.
const ARCHIVED_PAGE_SIZE = 20;
const ARCHIVED_CACHE_TTL_MS = 60_000;
interface ArchivedCacheEntry {
  sessions: DevSession[];
  fetchedAt: number;
  nextCursor: string | null;
  exhausted: boolean;
}
const archivedCache = new Map<string, ArchivedCacheEntry>();
// Per-channel toggle so visiting another channel + coming back keeps state.
const archivedOpenState = new Map<string, boolean>();

function isArchivedOpen(channelId: string): boolean {
  return archivedOpenState.get(channelId) ?? false;
}

function setArchivedOpen(channelId: string, open: boolean): void {
  archivedOpenState.set(channelId, open);
}

function invalidateArchivedCache(channelId: string): void {
  archivedCache.delete(channelId);
}

function cursorFor(s: DevSession): string {
  return `${s.started_at}|${s.id}`;
}

function esc(str: string): string {
  const el = document.createElement('div');
  el.textContent = str;
  return el.innerHTML;
}

function escAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function clampTitle(v: string): string {
  return v.slice(0, 80);
}

export function renderDevsLaunch(body: HTMLElement): void {
  // Reset composer state each mount so Escape-collapse works across revisits.
  composerState = defaultState();

  body.classList.add('dl-body');
  body.innerHTML = `
    <div class="dl-scroll">
      <section class="dl-composer" id="dl-composer" aria-labelledby="dl-composer-title">
        ${composerCardHtml()}
      </section>
      <section class="dl-feed" id="dl-feed" aria-live="polite">
        <div class="dl-feed-loading">Loading sessions…</div>
      </section>
    </div>
  `;

  wireComposer(body);
  void refreshFeed(body);
  void refreshWorkers(body);

  if (feedTimer) clearInterval(feedTimer);
  feedTimer = setInterval(() => {
    if (!body.isConnected) {
      if (feedTimer) clearInterval(feedTimer);
      feedTimer = null;
      return;
    }
    if (document.visibilityState !== 'visible') return;
    void refreshFeed(body);
  }, 15_000);

  if (workersTimer) clearInterval(workersTimer);
  workersTimer = setInterval(() => {
    if (!body.isConnected) {
      if (workersTimer) clearInterval(workersTimer);
      workersTimer = null;
      return;
    }
    if (document.visibilityState !== 'visible') return;
    void refreshWorkers(body);
  }, 10_000);

  // Fetch briefs lazily the first time the user switches to the file tab.
}

export function cleanupDevsLaunch(): void {
  if (feedTimer) { clearInterval(feedTimer); feedTimer = null; }
  if (workersTimer) { clearInterval(workersTimer); workersTimer = null; }
}

// ── composer card ──────────────────────────────────────────────────────────

function composerCardHtml(): string {
  const s = composerState;
  const goDisabled = !canSubmit(s);

  return `
    <div class="dl-card">
      <header class="dl-card-head">
        <h2 class="dl-card-title" id="dl-composer-title">New dev session</h2>
        <button class="dl-card-collapse" id="dl-composer-esc" type="button"
          title="Collapse (Esc)" aria-label="Collapse composer">&times;</button>
      </header>
      <div class="dl-field">
        <label class="dl-label" for="dl-title">Title <span class="dl-required">*</span></label>
        <input type="text" class="dl-input" id="dl-title" maxlength="80" autocomplete="off"
          placeholder="What should this session do?" value="${escAttr(s.title)}">
        <div class="dl-hint"><span id="dl-title-count">${s.title.length}</span> / 80</div>
      </div>
      <div class="dl-field">
        <label class="dl-label">Brief <span class="dl-required">*</span></label>
        <div class="dl-tabs" role="tablist">
          <button class="dl-tab ${s.tab === 'inline' ? 'dl-tab-active' : ''}"
            role="tab" aria-selected="${s.tab === 'inline'}" data-tab="inline" type="button">Paste</button>
          <button class="dl-tab ${s.tab === 'file' ? 'dl-tab-active' : ''}"
            role="tab" aria-selected="${s.tab === 'file'}" data-tab="file" type="button">File</button>
        </div>
        <div class="dl-tab-body" data-tab-body="inline" style="${s.tab === 'inline' ? '' : 'display:none'}">
          <textarea class="dl-textarea" id="dl-brief-inline" rows="6"
            placeholder="Paste the full brief here. The first message in the new thread will be this content.">${esc(s.briefInline)}</textarea>
          <div class="dl-hint">${s.briefInline.length} chars / 262144 max</div>
        </div>
        <div class="dl-tab-body" data-tab-body="file" style="${s.tab === 'file' ? '' : 'display:none'}">
          <input type="text" class="dl-input" id="dl-brief-filter"
            placeholder="Filter (fuzzy): type to narrow the list" value="${escAttr(s.briefFilter)}">
          <div class="dl-brief-list" id="dl-brief-list" role="listbox" aria-label="Available briefs">
            <div class="dl-brief-loading">Loading briefs…</div>
          </div>
          <div class="dl-hint" id="dl-brief-selected" aria-live="polite">
            ${s.briefPath ? `Selected: <code>${esc(s.briefPath)}</code>` : 'No brief selected.'}
          </div>
        </div>
      </div>
      <div class="dl-row">
        <div class="dl-field dl-field-half">
          <label class="dl-label" for="dl-worker">Target worker</label>
          <select class="dl-input" id="dl-worker">
            <option value="any-idle" ${s.worker === 'any-idle' ? 'selected' : ''}>Any idle (auto-pick)</option>
            <option value="dev1" ${s.worker === 'dev1' ? 'selected' : ''}>dev1 — Opus</option>
            <option value="dev2" ${s.worker === 'dev2' ? 'selected' : ''}>dev2 — Sonnet</option>
          </select>
          <div class="dl-hint">Auto-picks any idle worker, or reserves the chosen one. If none are idle, you'll be queued.</div>
        </div>
        <div class="dl-field dl-field-half">
          <label class="dl-label">Permission mode</label>
          <div class="dl-radio-group" role="radiogroup" aria-label="Permission mode">
            ${permissionRadio('full_write',    'Full write',     s.permission)}
            ${permissionRadio('read_plan',     'Read + plan',    s.permission)}
            ${permissionRadio('ask_per_action','Ask per action', s.permission)}
          </div>
        </div>
      </div>
      <div class="dl-actions">
        <button class="dl-btn dl-btn-primary" id="dl-go"
          ${goDisabled ? 'disabled' : ''} type="button">
          ${s.submitting ? 'Starting…' : 'Go'}
        </button>
        <span class="dl-actions-hint" id="dl-actions-hint"></span>
      </div>
      <div class="dl-worker-chips" id="dl-worker-chips" aria-label="Worker pool status">
        ${workerChipRowHtml()}
      </div>
    </div>
  `;
}

function workerChipRowHtml(): string {
  const snap = workersCache;
  if (!snap) {
    return `<span class="dl-worker-chip-hint">Loading workers…</span>`;
  }
  if (snap.workers.length === 0) {
    return `<span class="dl-worker-chip-hint">No workers discovered.</span>`;
  }
  const depth = snap.queue_depth > 0
    ? `<span class="dl-worker-queue-pill" title="Queue depth">queue: ${snap.queue_depth}</span>`
    : '';
  const chips = snap.workers.map((w) => {
    const color = workerStateColor(w.state);
    const title = workerChipTooltip(w);
    return `<span class="dl-worker-chip" title="${escAttr(title)}">
      <span class="ds-dot ds-dot-${color}" aria-hidden="true"></span>
      <span class="dl-worker-chip-name">${esc(w.name)}</span>
    </span>`;
  }).join('');
  return `${chips}${depth}`;
}

function workerStateColor(s: WorkerSlot['state']): DevStatusColor {
  switch (s) {
    case 'idle': return 'green';
    case 'busy': return 'amber';
    case 'unhealthy': return 'red';
    case 'unreachable': return 'grey';
  }
}

function workerChipTooltip(w: WorkerSlot): string {
  const parts: string[] = [w.name, `state: ${w.state}`];
  if (w.current_session_title) parts.push(`running: ${w.current_session_title}`);
  if (w.unhealthy_reason) parts.push(`reason: ${w.unhealthy_reason}`);
  if (!w.memory_mcp_ok) parts.push('memory MCP missing');
  return parts.join(' · ');
}

function permissionRadio(value: PermissionMode, label: string, current: PermissionMode): string {
  const checked = value === current ? 'checked' : '';
  return `<label class="dl-radio">
    <input type="radio" name="dl-perm" value="${value}" ${checked}>
    <span>${esc(label)}</span>
  </label>`;
}

function canSubmit(s: ComposerState): boolean {
  if (s.submitting) return false;
  if (s.title.trim().length === 0) return false;
  if (s.tab === 'inline' && s.briefInline.trim().length === 0) return false;
  if (s.tab === 'file'   && !s.briefPath) return false;
  return true;
}

// ── composer wiring ────────────────────────────────────────────────────────

function wireComposer(root: HTMLElement): void {
  const reRender = (): void => {
    const mount = root.querySelector<HTMLElement>('#dl-composer');
    if (!mount) return;
    mount.innerHTML = composerCardHtml();
    wireComposer(root);
  };

  const titleEl = root.querySelector<HTMLInputElement>('#dl-title');
  titleEl?.addEventListener('input', () => {
    composerState.title = clampTitle(titleEl.value);
    const cnt = root.querySelector<HTMLElement>('#dl-title-count');
    if (cnt) cnt.textContent = String(composerState.title.length);
    updateGoState(root);
  });

  const escBtn = root.querySelector<HTMLButtonElement>('#dl-composer-esc');
  escBtn?.addEventListener('click', () => collapseComposer(root));
  // Esc inside the card also collapses.
  root.querySelector<HTMLElement>('#dl-composer')?.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Escape') collapseComposer(root);
  });

  // Tab switching.
  root.querySelectorAll<HTMLButtonElement>('.dl-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tab = (btn.dataset['tab'] as ComposerTab | undefined) ?? 'inline';
      composerState.tab = tab;
      reRender();
      if (tab === 'file' && briefsCache === null) {
        void loadBriefs(root);
      } else if (tab === 'file' && briefsCache) {
        paintBriefList(root);
      }
    });
  });

  const inlineEl = root.querySelector<HTMLTextAreaElement>('#dl-brief-inline');
  inlineEl?.addEventListener('input', () => {
    composerState.briefInline = inlineEl.value;
    updateGoState(root);
  });

  const filterEl = root.querySelector<HTMLInputElement>('#dl-brief-filter');
  filterEl?.addEventListener('input', () => {
    composerState.briefFilter = filterEl.value;
    paintBriefList(root);
  });

  const workerEl = root.querySelector<HTMLSelectElement>('#dl-worker');
  workerEl?.addEventListener('change', () => {
    composerState.worker = (workerEl.value as WorkerHint);
  });

  root.querySelectorAll<HTMLInputElement>('input[name="dl-perm"]').forEach((radio) => {
    radio.addEventListener('change', () => {
      if (radio.checked) composerState.permission = radio.value as PermissionMode;
    });
  });

  const go = root.querySelector<HTMLButtonElement>('#dl-go');
  go?.addEventListener('click', () => { void submitComposer(root); });

  // If we're on the file tab at render time, populate the list.
  if (composerState.tab === 'file') {
    if (briefsCache === null) void loadBriefs(root);
    else paintBriefList(root);
  }
}

function updateGoState(root: HTMLElement): void {
  const go = root.querySelector<HTMLButtonElement>('#dl-go');
  if (!go) return;
  go.disabled = !canSubmit(composerState);
}

function collapseComposer(root: HTMLElement): void {
  // Collapse shrinks the composer to a single "+ New session" strip.
  const mount = root.querySelector<HTMLElement>('#dl-composer');
  if (!mount) return;
  mount.innerHTML = `
    <button class="dl-reopen" id="dl-reopen" type="button">
      <span class="dl-reopen-plus">+</span> New dev session
    </button>`;
  root.querySelector<HTMLButtonElement>('#dl-reopen')?.addEventListener('click', () => {
    composerState = defaultState();
    mount.innerHTML = composerCardHtml();
    wireComposer(root);
    root.querySelector<HTMLInputElement>('#dl-title')?.focus();
  });
}

// ── brief picker ───────────────────────────────────────────────────────────

async function loadBriefs(root: HTMLElement): Promise<void> {
  try {
    briefsCache = await get<BriefListing[]>('/api/dev-sessions/available-briefs');
  } catch (e) {
    briefsCache = [];
    showToast(`Failed to load briefs: ${String(e)}`, 'err');
  }
  paintBriefList(root);
}

function paintBriefList(root: HTMLElement): void {
  const list = root.querySelector<HTMLElement>('#dl-brief-list');
  if (!list) return;
  const items = briefsCache ?? [];
  const q = composerState.briefFilter.trim().toLowerCase();

  const filtered = q
    ? items.filter((it) => fuzzyMatch(q, it.title.toLowerCase()) || fuzzyMatch(q, it.path.toLowerCase()))
    : items;

  if (filtered.length === 0) {
    list.innerHTML = `<div class="dl-brief-empty">${items.length === 0 ? 'No briefs found under infra/work/.' : 'No matches.'}</div>`;
    return;
  }

  list.innerHTML = filtered.map((it) => {
    const selected = it.path === composerState.briefPath ? 'dl-brief-item-selected' : '';
    const preview = it.preview.length > 160 ? it.preview.slice(0, 160) + '…' : it.preview;
    return `<button type="button" class="dl-brief-item ${selected}"
      role="option" aria-selected="${Boolean(selected)}"
      data-path="${escAttr(it.path)}">
      <div class="dl-brief-item-title">${esc(it.title)}</div>
      <div class="dl-brief-item-path"><code>${esc(it.path)}</code></div>
      <div class="dl-brief-item-preview">${esc(preview)}</div>
    </button>`;
  }).join('');

  list.querySelectorAll<HTMLButtonElement>('.dl-brief-item').forEach((btn) => {
    btn.addEventListener('click', () => {
      composerState.briefPath = btn.dataset['path'] ?? null;
      paintBriefList(root);
      const badge = root.querySelector<HTMLElement>('#dl-brief-selected');
      if (badge) {
        badge.innerHTML = composerState.briefPath
          ? `Selected: <code>${esc(composerState.briefPath)}</code>`
          : 'No brief selected.';
      }
      updateGoState(root);
    });
  });
}

/** Subsequence fuzzy match — every char of `needle` appears in `haystack` in order. */
function fuzzyMatch(needle: string, haystack: string): boolean {
  if (needle.length === 0) return true;
  let i = 0;
  for (let j = 0; j < haystack.length && i < needle.length; j++) {
    if (haystack[j] === needle[i]) i++;
  }
  return i === needle.length;
}

// ── submit ─────────────────────────────────────────────────────────────────

async function submitComposer(root: HTMLElement): Promise<void> {
  if (!canSubmit(composerState)) return;
  composerState.submitting = true;
  updateGoState(root);
  const go = root.querySelector<HTMLButtonElement>('#dl-go');
  if (go) go.textContent = 'Starting…';

  const ch = findCurrentChannelId();
  if (!ch) {
    composerState.submitting = false;
    updateGoState(root);
    showToast('Could not resolve #devs-launch channel id', 'err');
    return;
  }

  const payload: Record<string, unknown> = {
    title: composerState.title.trim(),
    worker_hint: composerState.worker,
    permission_mode: composerState.permission,
    channel_id: ch,
    created_by: state.ownerId ?? '',
  };
  if (composerState.tab === 'inline') {
    payload['brief_inline'] = composerState.briefInline;
  } else if (composerState.briefPath) {
    payload['brief_path'] = composerState.briefPath;
  }

  try {
    const resp = await post<{ session_id: string; thread_id: string }>(
      '/api/dev-sessions/start',
      payload,
    );
    // Immediately kick the runner. /start just inserts the row; /start-run
    // acquires a worker (or queues) and spawns claude.
    let runResp: { state: string; worker: string | null; queue_position: number | null } | null = null;
    try {
      runResp = await post<{ state: string; worker: string | null; queue_position: number | null }>(
        `/api/dev-sessions/${encodeURIComponent(resp.session_id)}/start-run`,
        { person_id: state.ownerId ?? '' },
      );
    } catch (e) {
      showToast(`Started (row created) but runner kick failed: ${String(e)}`, 'err');
    }
    if (runResp) {
      if (runResp.state === 'running' && runResp.worker) {
        showToast(`Started on ${runResp.worker}`, 'ok');
      } else if (runResp.state === 'queued') {
        const pos = runResp.queue_position ?? 0;
        showToast(`Queued — position ${pos}`, 'ok');
      } else {
        showToast('Session queued', 'ok');
      }
    } else {
      showToast('Session queued', 'ok');
    }
    // Reset composer to default + refresh the feed; then auto-scroll to top
    // so the newest session is visible.
    composerState = defaultState();
    const mount = root.querySelector<HTMLElement>('#dl-composer');
    if (mount) {
      mount.innerHTML = composerCardHtml();
      wireComposer(root);
    }
    await Promise.all([refreshFeed(root), refreshWorkers(root)]);
    root.querySelector<HTMLElement>('.dl-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
    if (resp.thread_id) {
      const hint = root.querySelector<HTMLElement>('#dl-actions-hint');
      if (hint) hint.textContent = 'Thread created. Click the new session card to open it.';
    }
  } catch (e) {
    composerState.submitting = false;
    updateGoState(root);
    if (go) go.textContent = 'Go';
    showToast(`Start failed: ${String(e)}`, 'err');
  }
}

function findCurrentChannelId(): string | null {
  const sid = state.currentServer;
  if (!sid) return null;
  const ch = state.channels.find(
    (c) => c.server_id === sid && c.name === 'devs-launch',
  );
  return ch?.id ?? null;
}

// ── worker chip row ────────────────────────────────────────────────────────

async function refreshWorkers(root: HTMLElement): Promise<void> {
  const personId = state.ownerId ?? '';
  if (!personId) return;
  try {
    const snap = await get<WorkersSnapshot>(
      `/api/dev-sessions/workers?person_id=${encodeURIComponent(personId)}`,
    );
    workersCache = snap;
  } catch {
    // Non-admins get a 403; hide the chip row silently in that case.
    workersCache = null;
  }
  paintWorkerChips(root);
}

function paintWorkerChips(root: HTMLElement): void {
  const chips = root.querySelector<HTMLElement>('#dl-worker-chips');
  if (!chips) return;
  if (!workersCache) {
    chips.style.display = 'none';
    return;
  }
  chips.style.display = '';
  chips.innerHTML = workerChipRowHtml();
}

// ── session feed ───────────────────────────────────────────────────────────

async function refreshFeed(root: HTMLElement): Promise<void> {
  const feed = root.querySelector<HTMLElement>('#dl-feed');
  if (!feed) return;
  const channelId = findCurrentChannelId();
  if (!channelId) {
    feed.innerHTML = `<div class="dl-feed-empty">Channel not loaded yet. Refresh to try again.</div>`;
    return;
  }
  let sessions: DevSession[] = [];
  try {
    sessions = await listDevSessions({ channel_id: channelId, limit: 50 });
  } catch (e) {
    feed.innerHTML = `<div class="dl-feed-empty">Failed to load sessions: ${esc(String(e))}</div>`;
    return;
  }
  const hasActive = sessions.some(
    (x) => x.status === 'running' || x.status === 'queued' || x.status === 'reviewing',
  );
  // Mitchell: hide the composer when there are active sessions — compact view.
  const composerEl = root.querySelector<HTMLElement>('#dl-composer');
  if (composerEl) composerEl.style.display = hasActive ? 'none' : '';
  const feedHeader = hasActive
    ? `<div class="dl-feed-header">
         <span class="dl-feed-header-count">${sessions.filter((x) => x.status === 'running' || x.status === 'queued' || x.status === 'reviewing').length} active</span>
         <button type="button" class="dl-feed-header-new" id="dl-reopen-composer">+ New session</button>
       </div>`
    : '';
  const active = sessions.length === 0
    ? `<div class="dl-feed-empty">No sessions yet. Fill in the composer above to start one.</div>`
    : `${feedHeader}<ol class="dl-feed-list" role="list">${sessions.map(sessionCardHtml).join('')}</ol>`;
  // Always render the archived header (even if there are zero active rows).
  // The header itself is cheap; the body is only fetched on expand.
  feed.innerHTML = `${active}${archivedSectionHtml(channelId)}`;
  // "+ New session" link inside the feed header unhides the composer.
  feed.querySelector<HTMLButtonElement>('#dl-reopen-composer')?.addEventListener('click', () => {
    const composer = root.querySelector<HTMLElement>('#dl-composer');
    if (composer) composer.style.display = '';
    composer?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
    feed.querySelectorAll<HTMLElement>('[data-open-thread]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const tid = btn.dataset['openThread'] || '';
      if (tid) void openThreadPanelById(tid);
    });
  });
  wireArchivedSection(root, channelId);
}

function sessionCardHtml(s: DevSession): string {
  const color = (s.status_color ?? 'grey') as DevStatusColor;
  const worker = s.worker_container ?? s.agent_target ?? '';
  const startedAgo = timeAgoShort(s.started_at);
  const summaryBits: string[] = [];
  if (s.status) summaryBits.push(`status: ${s.status}`);
  if (startedAgo) summaryBits.push(startedAgo);
  if (s.permission_mode) summaryBits.push(`perm: ${s.permission_mode}`);
  const summary = summaryBits.join(' · ');
  const threadAttr = s.thread_id ? `data-open-thread="${escAttr(s.thread_id)}"` : '';
  const role = s.thread_id ? 'button' : '';
  const tabindex = s.thread_id ? '0' : '';
  return `<li class="ds-session-header" role="listitem">
    <div class="ds-session-row ${s.thread_id ? 'ds-session-row-click' : ''}" ${threadAttr} role="${role}" ${tabindex ? `tabindex="${tabindex}"` : ''}>
      <span class="ds-dot ds-dot-${color}" title="${escAttr(s.status)}" aria-hidden="true"></span>
      <span class="ds-session-title">${esc(s.name)}</span>
      <span class="ds-worker-pill">${esc(worker)}</span>
      <span class="ds-session-summary">${esc(summary)}</span>
      ${s.thread_id ? `<span class="ds-open-thread-link">Open thread →</span>` : ''}
    </div>
  </li>`;
}

function timeAgoShort(iso: string): string {
  const d = parseIsoUtc(iso)?.getTime() ?? NaN;
  if (!Number.isFinite(d)) return '';
  const mins = Math.max(0, Math.floor((Date.now() - d) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Archived section ──────────────────────────────────────────────────────
//
// Collapsed by default. On expand we fetch page 1 and paint cards. Each card
// surfaces two admin actions:
//   • Un-archive   — confirm modal, then POST /un-archive, invalidate cache,
//                    refresh both feeds (the row now belongs to active).
//   • Retry MCP    — only shown when archive_status === 'frozen-error';
//                    POST /retry-mcp-writeback, surface error toast if any.
//
// The panel is keyboard-friendly: header has role=button, Enter/Space toggle.

function archivedSectionHtml(channelId: string): string {
  const open = isArchivedOpen(channelId);
  const cached = archivedCache.get(channelId);
  const bodyId = 'dl-archived-body';
  const headerId = 'dl-archived-header';
  const chevron = open ? '▾' : '▸';
  return `
    <section class="dl-archived" data-channel="${escAttr(channelId)}">
      <h3 id="${headerId}" class="dl-archived-header" role="button" tabindex="0"
          aria-expanded="${open}" aria-controls="${bodyId}">
        <span class="dl-archived-chevron" aria-hidden="true">${chevron}</span>
        <span class="dl-archived-title">Archived</span>
        <span class="dl-archived-subtle">(>=24h inactive · Memory MCP)</span>
      </h3>
      <div id="${bodyId}" class="dl-archived-body" role="region"
           aria-labelledby="${headerId}" ${open ? '' : 'hidden'}>
        ${open ? archivedListHtml(cached) : ''}
      </div>
    </section>
  `;
}

function archivedListHtml(cache: ArchivedCacheEntry | undefined): string {
  if (!cache) {
    return `<div class="dl-archived-loading">Loading archived sessions…</div>`;
  }
  if (cache.sessions.length === 0) {
    return `<div class="dl-archived-empty">No archived sessions yet.</div>`;
  }
  const items = cache.sessions.map(archivedCardHtml).join('');
  const more = cache.exhausted
    ? `<div class="dl-archived-end">End of archive.</div>`
    : `<button type="button" class="dl-archived-more" id="dl-archived-more">Load more</button>`;
  return `<ol class="dl-archived-list" role="list">${items}</ol>${more}`;
}

function archivedCardHtml(s: DevSession): string {
  const color = (s.status_color ?? 'grey') as DevStatusColor;
  const worker = s.worker_container ?? s.agent_target ?? '';
  const archivedAgo = s.archived_at ? timeAgoShort(s.archived_at) : '';
  const isFrozen = s.archive_status === 'frozen-error';
  const frozenBadge = isFrozen
    ? `<span class="dl-archived-frozen" title="${escAttr(s.archive_error ?? 'MCP writeback failed')}">frozen</span>`
    : '';
  const mcpNote = s.mcp_writeback_at
    ? `<span class="dl-archived-mcp ok">MCP ok · ${esc(timeAgoShort(s.mcp_writeback_at))}</span>`
    : `<span class="dl-archived-mcp">MCP: pending</span>`;
  const threadAttr = s.thread_id ? `data-open-thread="${escAttr(s.thread_id)}"` : '';
  const retryBtn = isFrozen
    ? `<button type="button" class="dl-archived-btn dl-archived-btn-retry"
         data-action="retry-mcp" data-session="${escAttr(s.id)}">Retry MCP writeback</button>`
    : '';
  return `<li class="dl-archived-card" role="listitem">
    <div class="dl-archived-row ${s.thread_id ? 'dl-archived-row-click' : ''}" ${threadAttr}>
      <span class="ds-dot ds-dot-${color}" title="${escAttr(s.status)}" aria-hidden="true"></span>
      <span class="dl-archived-name">${esc(s.name)}</span>
      <span class="ds-worker-pill">${esc(worker)}</span>
      ${frozenBadge}
      <span class="dl-archived-meta">
        <span>${esc(s.status)}</span>
        <span>·</span>
        <span title="Archived">archived ${esc(archivedAgo)}</span>
      </span>
      ${mcpNote}
    </div>
    ${isFrozen && s.archive_error ? `<div class="dl-archived-error"><code>${esc(truncateForUi(s.archive_error, 240))}</code></div>` : ''}
    <div class="dl-archived-actions">
      ${retryBtn}
      <button type="button" class="dl-archived-btn dl-archived-btn-unarc"
        data-action="un-archive" data-session="${escAttr(s.id)}">Un-archive</button>
    </div>
  </li>`;
}

function truncateForUi(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

function wireArchivedSection(root: HTMLElement, channelId: string): void {
  const section = root.querySelector<HTMLElement>('.dl-archived');
  if (!section) return;
  const header = section.querySelector<HTMLElement>('.dl-archived-header');
  const body = section.querySelector<HTMLElement>('.dl-archived-body');
  if (!header || !body) return;

  const toggle = (): void => {
    const open = !isArchivedOpen(channelId);
    setArchivedOpen(channelId, open);
    header.setAttribute('aria-expanded', String(open));
    const chev = header.querySelector<HTMLElement>('.dl-archived-chevron');
    if (chev) chev.textContent = open ? '▾' : '▸';
    if (open) {
      body.removeAttribute('hidden');
      void loadArchivedPage(root, channelId, /*append=*/ false);
    } else {
      body.setAttribute('hidden', '');
      body.innerHTML = '';
    }
  };

  header.addEventListener('click', toggle);
  header.addEventListener('keydown', (e) => {
    const ke = e as KeyboardEvent;
    if (ke.key === 'Enter' || ke.key === ' ') {
      e.preventDefault();
      toggle();
    }
  });

  if (isArchivedOpen(channelId)) {
    // Repaint async — the cached page may be stale (60s TTL).
    void loadArchivedPage(root, channelId, /*append=*/ false);
  }

  // Actions + load-more inside the body are wired after every paint.
  bindArchivedBodyHandlers(root, channelId);
}

async function loadArchivedPage(root: HTMLElement, channelId: string, append: boolean): Promise<void> {
  const body = root.querySelector<HTMLElement>('#dl-archived-body');
  if (!body) return;
  const cached = archivedCache.get(channelId);
  // Cache hit: serve-stale + trust TTL. If not appending and cache fresh, skip.
  if (!append && cached && Date.now() - cached.fetchedAt < ARCHIVED_CACHE_TTL_MS) {
    body.innerHTML = archivedListHtml(cached);
    bindArchivedBodyHandlers(root, channelId);
    return;
  }
  if (!append) {
    body.innerHTML = `<div class="dl-archived-loading">Loading archived sessions…</div>`;
  } else if (cached) {
    const btn = body.querySelector<HTMLButtonElement>('#dl-archived-more');
    if (btn) { btn.disabled = true; btn.textContent = 'Loading…'; }
  }

  const cursor = append && cached ? cached.nextCursor : null;
  let page: DevSession[] = [];
  try {
    page = await listDevSessions({
      channel_id: channelId,
      archived: true,
      limit: ARCHIVED_PAGE_SIZE,
      cursor: cursor ?? undefined,
    });
  } catch (e) {
    body.innerHTML = `<div class="dl-archived-empty">Failed to load archived sessions: ${esc(String(e))}</div>`;
    return;
  }

  const merged = append && cached ? [...cached.sessions, ...page] : page;
  const last = page[page.length - 1];
  const entry: ArchivedCacheEntry = {
    sessions: merged,
    fetchedAt: Date.now(),
    nextCursor: last ? cursorFor(last) : null,
    exhausted: page.length < ARCHIVED_PAGE_SIZE,
  };
  archivedCache.set(channelId, entry);
  body.innerHTML = archivedListHtml(entry);
  bindArchivedBodyHandlers(root, channelId);
}

function bindArchivedBodyHandlers(root: HTMLElement, channelId: string): void {
  const body = root.querySelector<HTMLElement>('#dl-archived-body');
  if (!body) return;

  body.querySelectorAll<HTMLElement>('.dl-archived-row[data-open-thread]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      // Don't open thread if the click landed on one of the action buttons.
      if (target.closest('.dl-archived-btn')) return;
      e.preventDefault();
      const tid = btn.dataset['openThread'] || '';
      if (tid) void openThreadPanelById(tid);
    });
  });

  body.querySelectorAll<HTMLButtonElement>('.dl-archived-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const sid = btn.dataset['session'] ?? '';
      const action = btn.dataset['action'] ?? '';
      if (!sid || !action) return;
      if (action === 'un-archive') {
        confirmModal(
          'Un-archive session',
          'This will move the session back to the active feed. The Memory MCP entity stays in place. Continue?',
          () => { void performUnArchive(root, channelId, sid); },
        );
      } else if (action === 'retry-mcp') {
        btn.disabled = true;
        btn.textContent = 'Retrying…';
        try {
          await retryMcpWriteback(sid);
          showToast('MCP writeback retried', 'ok');
          invalidateArchivedCache(channelId);
          await loadArchivedPage(root, channelId, false);
        } catch (err) {
          showToast(`Retry failed: ${String(err)}`, 'err');
          btn.disabled = false;
          btn.textContent = 'Retry MCP writeback';
        }
      }
    });
  });

  body.querySelector<HTMLButtonElement>('#dl-archived-more')?.addEventListener('click', () => {
    void loadArchivedPage(root, channelId, /*append=*/ true);
  });
}

async function performUnArchive(root: HTMLElement, channelId: string, sessionId: string): Promise<void> {
  try {
    await unArchiveDevSession(sessionId);
    showToast('Session un-archived', 'ok');
    invalidateArchivedCache(channelId);
    // Refresh both active + archived — the row just moved feeds.
    await refreshFeed(root);
    if (isArchivedOpen(channelId)) {
      await loadArchivedPage(root, channelId, false);
    }
  } catch (err) {
    showToast(`Un-archive failed: ${String(err)}`, 'err');
  }
}
